/**
 * Inbox search (#716): what a typed query matches, as one SQL predicate.
 *
 * `?q=` used to be `LIKE '%q%'` over five columns. An address in `submitter_email`, `to_address`
 * or `cc_address`, a Webflow submission's text (it lives only in `body_html`), the association's
 * name and the lot's owners were all invisible, so `kris.w.murphy@gmail.com` found nothing even
 * on the Archived tab.
 *
 * In `utils/` because both ends parse: the server to search, the client to know whether a query
 * has any words at all before offering "Search all mail".
 */

/** More words than this are ignored — a pasted paragraph is not a search. */
export const MAX_SEARCH_TERMS = 8;

/**
 * Lower-case A–Z ONLY — exactly what SQLite's built-in `lower()` does to the haystack. A full
 * Unicode `toLowerCase()` turned a typed `Élodie` into `élodie`, which `lower('Élodie')`
 * (still `Élodie`) never contains, so an exactly-typed accented name found nothing. Folding
 * both sides the same way keeps exact typing working, as the old LIKE search did.
 */
const foldAsciiCase = (text: string): string => text.replace(/[A-Z]/g, (c) => c.toLowerCase());

/**
 * The words to match: whitespace-separated, with `"quoted phrases"` kept whole. ASCII-folded,
 * de-duplicated, empty pieces dropped. An unclosed quote takes the rest of the line as a phrase.
 */
export function parseSearchTerms(raw: string | null | undefined): string[] {
	const text = String(raw ?? '');
	const terms: string[] = [];
	const pattern = /"([^"]*)"?|(\S+)/g;
	for (const match of text.matchAll(pattern)) {
		const term = foldAsciiCase((match[1] ?? match[2] ?? '').replace(/\s+/g, ' ').trim());
		if (term && !terms.includes(term)) terms.push(term);
		if (terms.length === MAX_SEARCH_TERMS) break;
	}
	return terms;
}

/**
 * Everything a message can be found by, as one string per row, ASCII-lower-cased by SQLite's
 * `lower()` (the terms are folded to match — see `foldAsciiCase`).
 *
 * Fields are joined with a unit separator (char 31), so a term can never match ACROSS two
 * fields ("smith" + "jones" must not become "smithjones"). The association name and the owners
 * of record on the matched lot come from scalar subqueries, so the search needs no JOIN and the
 * row count is untouched.
 */
const haystack = (alias: string): string => `lower(
	COALESCE(${alias}.subject, '') || char(31) ||
	COALESCE(${alias}.body_text, '') || char(31) ||
	COALESCE(${alias}.body_html, '') || char(31) ||
	COALESCE(${alias}.from_address, '') || char(31) ||
	COALESCE(${alias}.from_name, '') || char(31) ||
	COALESCE(${alias}.submitter_name, '') || char(31) ||
	COALESCE(${alias}.submitter_email, '') || char(31) ||
	COALESCE(${alias}.to_address, '') || char(31) ||
	COALESCE(${alias}.cc_address, '') || char(31) ||
	COALESCE(${alias}.property_address, '') || char(31) ||
	COALESCE((SELECT s_q.name FROM associations s_q WHERE s_q.id = ${alias}.association_id), '') || char(31) ||
	COALESCE((
		SELECT group_concat(COALESCE(p_q.first_name, '') || ' ' || COALESCE(p_q.last_name, '') || ' ' || COALESCE(p_q.email, ''), char(31))
		FROM account_people ap_q
		JOIN people p_q ON p_q.id = ap_q.person_id AND p_q.deleted_at IS NULL
		WHERE ap_q.account_id = ${alias}.account_id AND ap_q.role = 'owner' AND ap_q.deleted_at IS NULL
	), '')
)`;

/**
 * Every term must appear somewhere in the message, in any order.
 *
 * `instr`, not `LIKE`: D1 refuses a LIKE pattern over 50 bytes, and `_` / `%` — both legal in
 * an email address — would act as wildcards. One bind per term keeps a long query far under
 * D1's bind cap; the haystack is rebuilt per term, which at the inbox's size is negligible.
 *
 * @returns null when there is nothing to search for
 */
export function searchPredicate(terms: string[], alias = 'm'): { clause: string; binds: unknown[] } | null {
	if (terms.length === 0) return null;
	const hay = haystack(alias);
	return {
		clause: `(${terms.map(() => `instr(${hay}, ?) > 0`).join(' AND ')})`,
		binds: [...terms],
	};
}
